import { UserFriendlyError } from './user-friendly-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/CreditLimitError')

/**
 * Monthly agent credits have run out.
 *
 * Distinct from {@link AuthError} because nothing is wrong with the user's
 * permissions: the same request will succeed next period, or once someone
 * raises the limit. Which limit was hit decides who has to act, so it is
 * carried on {@link CreditLimitError.scope}.
 */
export class CreditLimitError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'CreditLimitError'
  public override readonly name: string = CreditLimitError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  constructor(
    /** Whose limit was reached. */
    public readonly scope: CreditLimitError.Scope,
    actualError?: Error,
  ) {
    super(
      scope === 'user'
        ? 'You have used all of your agent credits for this month. Ask an administrator to raise your limit.'
        : "Your organization has used all of its agent credits for this month. Contact your administrator.",
      actualError,
    )
  }
}
export namespace CreditLimitError {
  /**
   * `user` — this person's own cap. `tenant` — the shared pool, so every user
   * in the tenant is blocked.
   */
  export type Scope = 'tenant' | 'user'

  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isCreditLimitError(val: unknown): val is CreditLimitError {
    return !!(val as CreditLimitError)?.[MARKER_SYMBOL]
  }
}
